import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Avatar, Chip, CircularProgress, Paper, Button,
  Tooltip, LinearProgress, Select, MenuItem, InputBase, IconButton,
  Switch, FormControlLabel, useTheme,
} from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, LabelList,
  Tooltip as RTooltip, PieChart, Pie, Cell,
} from 'recharts';
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import PersonOffIcon        from '@mui/icons-material/PersonOff';
import BeachAccessIcon      from '@mui/icons-material/BeachAccess';
import RefreshIcon          from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PendingActionsIcon   from '@mui/icons-material/PendingActions';
import PeopleAltIcon        from '@mui/icons-material/PeopleAlt';
import PauseIcon            from '@mui/icons-material/Pause';
import SyncIcon             from '@mui/icons-material/Sync';
import WifiIcon             from '@mui/icons-material/Wifi';
import WifiOffIcon          from '@mui/icons-material/WifiOff';
import DownloadIcon         from '@mui/icons-material/Download';
import OpenInFullIcon       from '@mui/icons-material/OpenInFull';
import PersonIcon           from '@mui/icons-material/Person';
import BusinessIcon         from '@mui/icons-material/Business';
import WorkIcon             from '@mui/icons-material/Work';
import SearchIcon           from '@mui/icons-material/Search';
import {
  STATUS_CFG,
  eventsToPresence, statusFromPresence, workMinutes,
  formatHHMMSS, calcBreakMinutes, activeBreakMinutes,
  loadPresence, savePresence, todayKey, fmtTime,
} from '../components/presence/LivePresence';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Token bridge + auto-refresh ───────────────────────────────────────────────
// Priority: cookie (shared across all tabs, updated by main app on refresh)
//           → monitoring_token in localStorage (set when opening the window)
//           → sessionStorage (same tab only, fallback)
function readStoredToken() {
  try {
    // 1. Cookie — updated by the main app on every token refresh
    const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
    if (cookie) {
      const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
      const u = JSON.parse(val);
      const t = u?.token || u?.data?.token || '';
      if (t) return t;
    }
  } catch (_) {}
  try {
    // 2. monitoring_token — set by the parent window just before opening
    const t = localStorage.getItem('monitoring_token');
    if (t) return t;
  } catch (_) {}
  try {
    // 3. sessionStorage — only works if same tab opened monitoring URL
    const raw = sessionStorage.getItem('logedInUser') || '';
    if (raw) { const u = JSON.parse(raw); return u?.token || u?.data?.token || ''; }
  } catch (_) {}
  return '';
}

// Try to exchange the stored refreshToken for a new access token.
// Stores the new token in monitoring_token and the cookie (best-effort).
async function tryRefreshToken() {
  try {
    const rt = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken') || '';
    if (!rt) return null;
    const r = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    const data = await r.json();
    // Backend returns { success: true, data: { token, refreshToken } }
    const newToken = data?.data?.token || data?.token || data?.accessToken || '';
    if (!newToken) return null;
    // Update refresh token if rotated
    const newRefresh = data?.data?.refreshToken || '';
    if (newRefresh) {
      try { localStorage.setItem('refreshToken', newRefresh); } catch (_) {}
    }
    // Store for this window and for future tabs
    localStorage.setItem('monitoring_token', newToken);
    // Also update cookie if present
    const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
    if (cookie) {
      try {
        const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
        const u = JSON.parse(val);
        u.token = newToken;
        const d = new Date(); d.setTime(d.getTime() + 4 * 24 * 60 * 60 * 1000);
        document.cookie = `logedInUser=${encodeURIComponent(JSON.stringify(u))};expires=${d.toUTCString()};path=/`;
      } catch (_) {}
    }
    return newToken;
  } catch (_) { return null; }
}

// fetch() wrapper that auto-refreshes the token on 401
async function apiFetch(url, opts = {}) {
  const token = readStoredToken();
  const headers = { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const r = await fetch(url, { ...opts, headers });
  if (r.status === 401) {
    // Token expired — try refresh once
    const newToken = await tryRefreshToken();
    if (newToken) {
      const r2 = await fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${newToken}` } });
      return r2;
    }
  }
  return r;
}

// Emrat e shkurtër të ditëve për trendin. `toLocaleDateString` te sq-AL kthen
// "e mar" / "e mër" — dy shkronja që ngatërrohen lehtë në një ekran muri.
const WEEKDAY_SQ = ['Die', 'Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht'];

// Dita e punës që mat unaza e orëve. Nuk është rregull kompanie — është korniza
// e ekranit; prandaj shkruhet edhe poshtë unazës, që numri të mos lexohet si
// premtim i një orari zyrtar.
const DAY_START_H = 6;
const DAY_END_H   = 18;

// Kartelat e sipërme. `key` është pikërisht statusi, ndaj klikimi filtron pa
// përkthim; `all` është përjashtimi i vetëm.
const STAT_CARDS = [
  { key: 'all',     label: 'Të gjithë',           tone: null },
  { key: 'present', label: STATUS_CFG.present.label, tone: STATUS_CFG.present },
  { key: 'meeting', label: 'Takim',               tone: STATUS_CFG.meeting },
  { key: 'remote',  label: STATUS_CFG.remote.label, tone: STATUS_CFG.remote },
  { key: 'break',   label: 'Pauzë',               tone: STATUS_CFG.break },
  { key: 'lunch',   label: STATUS_CFG.lunch.label, tone: STATUS_CFG.lunch },
  { key: 'short',   label: STATUS_CFG.short.label, tone: STATUS_CFG.short },
  { key: 'left',    label: STATUS_CFG.left.label,  tone: STATUS_CFG.left },
  { key: 'absent',  label: STATUS_CFG.absent.label, tone: STATUS_CFG.absent },
  { key: 'leave',   label: STATUS_CFG.leave.label,  tone: STATUS_CFG.leave },
];

/** Kohëzgjatje, jo orë muri: `18h 57m`, që të mos ngatërrohet me sahatin. */
function humanDuration(mins = 0) {
  const m = Math.max(0, Math.round(mins));
  return `${Math.floor(m / 60)}h ${('0' + (m % 60)).slice(-2)}m`;
}

function LiveTimer({ checkIn }) {
  const [secs, setSecs] = useState(() =>
    checkIn ? Math.max(0, Math.floor((Date.now() - new Date(checkIn).getTime()) / 1000)) : 0
  );
  useEffect(() => {
    const id = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return <span>{h}:{('0' + m).slice(-2)}:{('0' + s).slice(-2)}</span>;
}

/** Kokë paneli: një etiketë e vetme, e njëjta në të katër panelet. */
function PanelTitle({ children, note }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
      <Typography sx={{
        fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'text.primary',
      }}>{children}</Typography>
      {note && (
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', letterSpacing: '0.02em' }}>
          {note}
        </Typography>
      )}
    </Stack>
  );
}

const Panel = ({ children, sx }) => (
  <Paper elevation={0} sx={{
    p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider',
    display: 'flex', flexDirection: 'column', ...sx,
  }}>{children}</Paper>
);

export default function PresenceMonitor() {
  const theme = useTheme();
  const [workers,     setWorkers]     = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [departments, setDepartments] = useState([]);
  const [presence,    setPresence]    = useState(() => loadPresence());
  const [agents,      setAgents]      = useState([]); // Nexuma Presence Agent (desktop) — live
  const [kiosks,      setKiosks]      = useState([]);
  const [trend,       setTrend]       = useState([]); // 7 ditët e fundit
  const [loading,     setLoading]     = useState(false);
  const [tick,        setTick]        = useState(0);
  const [clock,       setClock]       = useState(new Date());
  const [lastSync,    setLastSync]    = useState(null);
  const [auto,        setAuto]        = useState(true);

  // Filters
  const [fStatus,     setFStatus]     = useState('all');
  const [fProject,    setFProject]    = useState('all');
  const [fDept,       setFDept]       = useState('all');
  const [fLead,       setFLead]       = useState('all');
  const [fSearch,     setFSearch]     = useState('');

  const REFRESH_INTERVAL = 15;

  useEffect(() => {
    const id = setInterval(() => { setClock(new Date()); setTick(t => t + 1); }, 1000);
    return () => clearInterval(id);
  }, []);

  const refresh = useCallback(async (withSpinner = false) => {
    if (withSpinner) setLoading(true);
    try {
      const today = todayKey();
      const r = await apiFetch(`${API_BASE}/api/query/getAttendanceByDay?date=${today}`);
      const data = await r.json();
      const events = data?.success ? (data.data || []) : [];
      const srv = eventsToPresence(events);
      const local = loadPresence();
      const merged = Object.keys(srv).length > 0 ? { ...srv } : { ...local };
      if (Object.keys(srv).length > 0) {
        Object.entries(local).forEach(([id, e]) => { if (!merged[id]) merged[id] = e; });
      }
      setPresence(merged);
      savePresence(merged);
      setLastSync(new Date());
    } catch (_) {
      setPresence(loadPresence());
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, []);

  // Trendi: shtatë ditë ngjarjesh, të ndara ditë-për-ditë PARA se të kthehen në
  // prezencë. `eventsToPresence` i palos të gjitha ngjarjet në një ditë të
  // vetme, ndaj po t'i jepje gjithë intervalin do të nxirrte një ditë të vetme
  // të pamundur (hyrja e së hënës me daljen e së premtes).
  const refreshTrend = useCallback(async () => {
    try {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 6);
      const key = (d) => `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
      const r = await apiFetch(`${API_BASE}/api/query/getAttendanceRange?from=${key(from)}&to=${key(to)}`);
      const data = await r.json();
      const events = data?.success ? (data.data || []) : [];
      const buckets = {};
      events.forEach(ev => {
        const raw = ev?.At || ev?.at || ev?.Timestamp || ev?.timestamp || ev?.created_at;
        const at = raw ? new Date(raw) : null;
        if (!at || isNaN(at)) return;
        (buckets[key(at)] = buckets[key(at)] || []).push(ev);
      });
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = key(d);
        const pres = eventsToPresence(buckets[k] || []);
        days.push({
          key: k,
          label: WEEKDAY_SQ[d.getDay()],
          attended: Object.values(pres).filter(p => p.checkIn).length,
        });
      }
      setTrend(days);
    } catch (_) { /* trendi është shtesë: pa të, faqja rri e plotë */ }
  }, []);

  useEffect(() => {
    apiFetch(`${API_BASE}/api/workers`)
      .then(r => r.json()).then(d => { if (d?.success) setWorkers(d.data || []); }).catch(() => {});
    apiFetch(`${API_BASE}/api/projects`)
      .then(r => r.json()).then(d => {
        if (d?.success) setProjects(d.data || []);
        else if (Array.isArray(d)) setProjects(d);
      }).catch(() => {});
    apiFetch(`${API_BASE}/api/department`)
      .then(r => r.json()).then(d => { if (d?.success) setDepartments(d.data || []); })
      .catch(() => {
        apiFetch(`${API_BASE}/api/departments`)
          .then(r => r.json()).then(d => { if (d?.success) setDepartments(d.data || []); }).catch(() => {});
      });
    refresh(true);
    refreshTrend();
  }, [refresh, refreshTrend]);

  useEffect(() => {
    if (!auto) return;
    if (tick > 0 && tick % REFRESH_INTERVAL === 0) refresh(false);
    // Trendi ndryshon një herë në ditë; mjafton çdo pesë minuta.
    if (tick > 0 && tick % 300 === 0) refreshTrend();
  }, [tick, auto, refresh, refreshTrend]);

  // ── Kiosqet dhe agjentët desktop ─────────────────────────────────────────
  useEffect(() => {
    const load = () => {
      apiFetch(`${API_BASE}/api/agent/presence`)
        .then(r => r.json()).then(d => { if (d?.success) setAgents(d.devices || []); }).catch(() => {});
      apiFetch(`${API_BASE}/api/kiosk/status`)
        .then(r => r.json()).then(d => setKiosks(d?.devices || [])).catch(() => {});
    };
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const derived = useMemo(() => workers.map(w => {
    const id  = String(w.Id || w.id);
    const p   = presence[id] || {};
    const status = statusFromPresence(p);
    const workM  = workMinutes(p);

    const workerProjectIds = (w.ProjectIds || w.projectIds || []).map(String);
    const workerProjects = projects.filter(pr => {
      const prId = String(pr.id || pr.Id);
      const members = (pr.members || pr.Members || []).map(String);
      return workerProjectIds.includes(prId) || members.includes(id);
    });

    const rawDept = w.Department || w.department || '';
    const deptObj = departments.find(d => String(d.Id || d.id) === String(rawDept));
    const deptName = deptObj?.Name || deptObj?.name ||
      (rawDept && !/^\d+$/.test(String(rawDept).trim()) ? rawDept : '');

    const teamLeads = [...new Set(
      workerProjects.map(pr => pr.lead_name || pr.LeadName || '').filter(Boolean)
    )];

    return { id, w, p, status, workM, workerProjects, deptName, teamLeads };
  }), [workers, presence, projects, departments, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const c = { all: derived.length };
    Object.keys(STATUS_CFG).forEach(k => { c[k] = 0; });
    derived.forEach(it => { if (c[it.status] !== undefined) c[it.status]++; });
    c.inWork    = c.present + c.meeting + c.remote;
    c.totalWork = derived.reduce((a, it) => a + it.workM, 0);
    return c;
  }, [derived]);

  const attendancePct = derived.length ? Math.round((totals.inWork / derived.length) * 100) : 0;

  // Unaza e orëve mat sa ka ecur dita e punës, jo sa punë është bërë — numri në
  // mes e thotë atë. Pa këtë ndarje, një ekran në orën 9:00 do të dukej sikur
  // kompania ka mbetur prapa.
  const dayPct = (() => {
    const mins = clock.getHours() * 60 + clock.getMinutes();
    const span = (DAY_END_H - DAY_START_H) * 60;
    return Math.max(0, Math.min(100, Math.round(((mins - DAY_START_H * 60) / span) * 100)));
  })();

  const kioskUi = kiosks.length === 0
    ? { label: 'Pa kiosk',       note: 'Asnjë pajisje e regjistruar', color: 'text.secondary', bg: 'grey.100',      Icon: WifiOffIcon }
    : kiosks.some(k => k.online)
      ? { label: 'Kiosk aktiv',  note: `${kiosks.filter(k => k.online).length} nga ${kiosks.length} online`, color: 'success.onSoft', bg: 'success.soft', Icon: WifiIcon }
      : { label: 'Kiosk offline', note: `${kiosks.length} pajisje, asnjë online`, color: 'error.onSoft', bg: 'error.soft', Icon: WifiOffIcon };

  const leadOptions = useMemo(() => {
    const names = new Set();
    projects.forEach(pr => { if (pr.lead_name || pr.LeadName) names.add(pr.lead_name || pr.LeadName); });
    return [...names].sort();
  }, [projects]);

  const filtered = useMemo(() => derived.filter(it => {
    if (fStatus !== 'all' && it.status !== fStatus) return false;
    if (fProject !== 'all') {
      const ids = it.workerProjects.map(pr => String(pr.id || pr.Id));
      if (!ids.includes(fProject)) return false;
    }
    if (fDept !== 'all' && it.deptName !== fDept) return false;
    if (fLead !== 'all' && !it.teamLeads.includes(fLead)) return false;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      const name = `${it.w.FirstName || ''} ${it.w.LastName || ''}`.toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  }), [derived, fStatus, fProject, fDept, fLead, fSearch]);

  function exportList() {
    const lines = [['Punëtori', 'Departamenti', 'Statusi', 'Hyrja', 'Dalja', 'Orë pune'].join(',')];
    filtered.forEach(it => {
      const cfg = STATUS_CFG[it.status] || STATUS_CFG.absent;
      const vals = [
        `${it.w?.FirstName || ''} ${it.w?.LastName || ''}`.trim(), it.deptName || '', cfg.label,
        fmtTime(it.p.checkIn) || '—', fmtTime(it.p.checkOut) || '—', formatHHMMSS(it.workM * 60),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(vals.join(','));
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    a.download = `monitorim_${todayKey()}.csv`;
    a.click();
  }

  const toggleFullscreen = () => {
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen();
    } catch (_) {}
  };

  const clockStr = `${('0' + clock.getHours()).slice(-2)}:${('0' + clock.getMinutes()).slice(-2)}:${('0' + clock.getSeconds()).slice(-2)}`;
  const hasFilter = fStatus !== 'all' || fProject !== 'all' || fDept !== 'all' || fLead !== 'all' || fSearch;

  const selectSx = {
    fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary',
    bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
    borderRadius: 2, height: 40, px: 1.25, minWidth: 200,
    '& .MuiSelect-select': { py: 0, pl: 0.5, pr: '28px !important', display: 'flex', alignItems: 'center' },
    '& .MuiSelect-icon': { color: 'text.disabled' },
    '&:hover': { borderColor: 'primary.main' },
  };

  const donut = [
    { name: STATUS_CFG.present.label, value: totals.present,                    fill: STATUS_CFG.present.color },
    { name: 'Takim/Distancë',         value: totals.meeting + totals.remote,    fill: STATUS_CFG.meeting.color },
    { name: 'Pauzë',                  value: totals.break + totals.lunch + totals.short, fill: STATUS_CFG.break.color },
    { name: STATUS_CFG.left.label,    value: totals.left,                       fill: STATUS_CFG.left.color },
    { name: STATUS_CFG.absent.label,  value: totals.absent + totals.leave,      fill: STATUS_CFG.absent.color },
  ].filter(d => d.value > 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── Koka ─────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ px: 2.5, py: 1.75, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{
                width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                bgcolor: 'primary.soft', color: 'primary.main', display: 'grid', placeItems: 'center',
              }}>
                <PendingActionsIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, letterSpacing: '-0.01em', color: 'text.primary' }}>
                Nexuma
              </Typography>
            </Stack>
            <Box sx={{ width: '1px', height: 34, bgcolor: 'divider' }} />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{
                  fontSize: '1.125rem', fontWeight: 800, letterSpacing: '0.02em',
                  textTransform: 'uppercase', color: 'text.primary',
                }}>
                  Monitorim Live
                </Typography>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.875, py: 0.25,
                  borderRadius: 1.5, bgcolor: 'success.soft', color: 'success.onSoft',
                }}>
                  <FiberManualRecordIcon sx={{
                    fontSize: 7,
                    animation: 'livePulse 1.6s infinite',
                    '@keyframes livePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                  }} />
                  <Typography component="span" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                    LIVE
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.625} alignItems="center" sx={{ mt: 0.125 }}>
                <SyncIcon sx={{
                  fontSize: 12, color: 'text.disabled',
                  ...(loading ? { animation: 'nxSpin 1s linear infinite', '@keyframes nxSpin': { to: { transform: 'rotate(360deg)' } } } : {}),
                }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {lastSync ? `Sinkronizuar ${fmtTime(lastSync)}` : 'Duke sinkronizuar…'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary', textTransform: 'capitalize' }}>
              {clock.toLocaleDateString('sq-AL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
            <Typography sx={{
              fontSize: '1.5rem', fontWeight: 800, color: 'text.primary',
              fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em', lineHeight: 1,
            }}>
              {clockStr}
            </Typography>
            <FormControlLabel
              control={<Switch size="small" checked={auto} onChange={e => setAuto(e.target.checked)} />}
              label={<Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>Auto</Typography>}
              labelPlacement="start"
              sx={{
                m: 0, px: 1.25, py: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                '& .MuiFormControlLabel-label': { mr: 1 },
              }} />
            <Tooltip title="Rifresko">
              <span>
                <IconButton onClick={() => { refresh(true); refreshTrend(); }} disabled={loading}
                  sx={{ width: 40, height: 40, borderRadius: 2, border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 19 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Ekran i plotë">
              <IconButton onClick={toggleFullscreen}
                sx={{ width: 40, height: 40, borderRadius: 2, border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>
                <OpenInFullIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* ── Kartelat e statuseve ──────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))' }}>
        {STAT_CARDS.map(({ key, label, tone }) => {
          const count = key === 'all' ? derived.length : (totals[key] ?? 0);
          const pct = derived.length ? Math.round((count / derived.length) * 100) : 0;
          const active = fStatus === key;
          const color = tone ? tone.color : 'primary.onSoft';
          return (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => setFStatus(key === 'all' ? 'all' : (active ? 'all' : key))}
              aria-pressed={active}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1, m: 0, p: 1.75, textAlign: 'left',
                font: 'inherit', cursor: 'pointer', borderRadius: 3,
                bgcolor: 'background.paper', border: '1px solid',
                borderColor: active ? (tone ? tone.color : 'primary.main') : 'divider',
                transition: theme.motion.hover,
                '&:hover': { borderColor: tone ? tone.color : 'primary.main' },
                ...theme.focusRing,
              }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{
                  width: 34, height: 34, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
                  bgcolor: tone ? tone.bg : 'primary.soft', color,
                }}>
                  {tone
                    ? React.cloneElement(tone.icon, { sx: { fontSize: 17 } })
                    : <PeopleAltIcon sx={{ fontSize: 17 }} />}
                </Box>
                <Typography sx={{
                  fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: count > 0 ? color : 'text.secondary', lineHeight: 1.3,
                }}>
                  {label}
                </Typography>
              </Stack>
              <Box>
                <Typography sx={{
                  fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums', color: count > 0 ? 'text.primary' : 'text.disabled',
                }}>
                  {count}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: count > 0 ? color : 'text.disabled' }}>
                  {key === 'all' ? 'Punonjës' : `${pct}%`}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={key === 'all' ? 100 : pct}
                sx={{
                  height: 3, borderRadius: 2, bgcolor: 'grey.100',
                  '& .MuiLinearProgress-bar': { bgcolor: tone ? tone.color : 'primary.main', borderRadius: 2 },
                }} />
            </Box>
          );
        })}
      </Box>

      {/* ── Katër panelet ────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' },
        '@media (min-width:1400px)': { gridTemplateColumns: '1fr 1.7fr 1fr 1fr' },
      }}>

        {/* Prezenca sot */}
        <Panel>
          <PanelTitle>Prezencë sot</PanelTitle>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
            <Box sx={{ position: 'relative', width: 132, height: 132, flexShrink: 0 }}>
              <PieChart width={132} height={132}>
                <Pie data={donut.length ? donut : [{ name: '—', value: 1, fill: theme.palette.grey[200] }]}
                  dataKey="value" cx={62} cy={62} innerRadius={44} outerRadius={64}
                  paddingAngle={donut.length > 1 ? 2 : 0}
                  startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
                  {(donut.length ? donut : [{ fill: theme.palette.grey[200] }]).map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Pie>
              </PieChart>
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {attendancePct}%
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Prezencë</Typography>
                </Box>
              </Box>
            </Box>
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              {[
                { label: STATUS_CFG.present.label, value: totals.present, color: STATUS_CFG.present.color },
                { label: 'Takim/Distancë', value: totals.meeting + totals.remote, color: STATUS_CFG.meeting.color },
                { label: STATUS_CFG.absent.label, value: totals.absent, color: STATUS_CFG.absent.color },
              ].map(r => (
                <Stack key={r.label} direction="row" spacing={1} alignItems="baseline">
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: r.color, minWidth: 28, fontVariantNumeric: 'tabular-nums' }}>
                    {r.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }} noWrap>{r.label}</Typography>
                </Stack>
              ))}
              <Box sx={{ pt: 1, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={0.875} alignItems="center">
                  <PeopleAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>{totals.inWork} / {derived.length}</Box> në punë
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Panel>

        {/* Trendi */}
        <Panel>
          <PanelTitle note="(7 ditët e fundit)">Trendi i prezencës</PanelTitle>
          <Box sx={{ flex: 1, minHeight: 168 }}>
            {trend.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>
                  Të dhënat e ditëve të kaluara nuk u lexuan dot
                </Typography>
              </Stack>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend.map(d => ({
                  ...d, pct: derived.length ? Math.round((d.attended / derived.length) * 100) : 0,
                }))} margin={{ top: 22, right: 14, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="nxTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={theme.palette.primary.main} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                  {/* `padding` i largon pikat e para/fundit nga buza, që etiketa
                      e tyre të mos hipë mbi boshtin Y. */}
                  <XAxis dataKey="label" tickLine={false} axisLine={false} padding={{ left: 14, right: 14 }}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false}
                    tick={{ fontSize: 11, fill: theme.palette.text.disabled }} tickFormatter={v => `${v}%`} />
                  <RTooltip formatter={(v) => [`${v}%`, 'Prezencë']}
                    contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}`, fontSize: 12 }} />
                  <Area type="monotone" dataKey="pct" stroke={theme.palette.primary.main} strokeWidth={2}
                    fill="url(#nxTrend)" isAnimationActive={false}
                    dot={{ r: 3, fill: theme.palette.primary.main, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}>
                    <LabelList dataKey="pct" position="top" offset={10}
                      formatter={v => `${v}%`}
                      style={{ fontSize: 11, fontWeight: 700, fill: theme.palette.text.primary }} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Panel>

        {/* Orë pune sot */}
        <Panel>
          <PanelTitle>Orë pune sot</PanelTitle>
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress variant="determinate" value={100} size={132} thickness={3}
                sx={{ color: 'grey.200' }} />
              <CircularProgress variant="determinate" value={dayPct} size={132} thickness={3}
                sx={{
                  color: 'primary.main', position: 'absolute', left: 0,
                  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                }} />
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {humanDuration(totals.totalWork)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                    Nga {derived.length} punonjës
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                {DAY_START_H}:00
              </Typography>
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                dita e punës
              </Typography>
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                {DAY_END_H}:00
              </Typography>
            </Stack>
          </Stack>
        </Panel>

        {/* Kioska + sinkronizimi */}
        <Panel>
          <PanelTitle>Statusi i kioskës</PanelTitle>
          <Stack direction="row" spacing={1.5} alignItems="center"
            sx={{ p: 1.5, borderRadius: 2, bgcolor: kioskUi.bg }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
              bgcolor: 'background.paper', color: kioskUi.color }}>
              <kioskUi.Icon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: kioskUi.color, lineHeight: 1.3 }}>
                {kioskUi.label}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{kioskUi.note}</Typography>
            </Box>
          </Stack>

          <Typography sx={{
            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'text.primary', mt: 1, mb: 1,
          }}>
            Sinkronizimi
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
              bgcolor: auto ? 'success.soft' : 'grey.100', color: auto ? 'success.onSoft' : 'text.secondary' }}>
              {auto ? <CheckCircleIcon sx={{ fontSize: 19 }} /> : <PauseIcon sx={{ fontSize: 19 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.3,
                color: auto ? 'success.onSoft' : 'text.secondary' }}>
                {auto ? 'Aktiv' : 'I ndalur'}
              </Typography>
              <Stack direction="row" spacing={0.625} alignItems="center">
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: auto ? 'success.main' : 'grey.400' }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {auto ? `çdo ${REFRESH_INTERVAL}s · ${lastSync ? fmtTime(lastSync) : '—'}` : 'vetëm me dorë'}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Panel>
      </Box>

      {/* ── Filtrat ──────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          borderRadius: 2, px: 1.5, height: 40, minWidth: 260, flex: { xs: '1 1 100%', md: '0 1 320px' },
          '&:focus-within': { borderColor: 'primary.main' },
        }}>
          <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <InputBase value={fSearch} onChange={e => setFSearch(e.target.value)}
            placeholder="Kërko punëtor…" sx={{ fontSize: '0.8125rem', flex: 1, '& input': { p: 0 } }} />
        </Stack>

        <Select value={fProject} onChange={e => setFProject(e.target.value)} input={<InputBase />} sx={selectSx}
          renderValue={(v) => {
            const pr = projects.find(x => String(x.id || x.Id) === String(v));
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <WorkIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.8125rem', color: pr ? 'text.primary' : 'text.secondary' }} noWrap>
                  {pr ? (pr.name || pr.Name) : 'Të gjitha projektet'}
                </Typography>
              </Stack>
            );
          }}>
          <MenuItem value="all">Të gjitha projektet</MenuItem>
          {projects.map(pr => (
            <MenuItem key={pr.id || pr.Id} value={String(pr.id || pr.Id)}>{pr.name || pr.Name}</MenuItem>
          ))}
        </Select>

        {departments.length > 0 && (
          <Select value={fDept} onChange={e => setFDept(e.target.value)} input={<InputBase />} sx={selectSx}
            renderValue={(v) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <BusinessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.8125rem', color: v === 'all' ? 'text.secondary' : 'text.primary' }} noWrap>
                  {v === 'all' ? 'Të gjitha departamentet' : v}
                </Typography>
              </Stack>
            )}>
            <MenuItem value="all">Të gjitha departamentet</MenuItem>
            {departments.map(d => {
              const nm = d.Name || d.name;
              return <MenuItem key={d.Id || d.id} value={nm}>{nm}</MenuItem>;
            })}
          </Select>
        )}

        {leadOptions.length > 0 && (
          <Select value={fLead} onChange={e => setFLead(e.target.value)} input={<InputBase />} sx={selectSx}
            renderValue={(v) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.8125rem', color: v === 'all' ? 'text.secondary' : 'text.primary' }} noWrap>
                  {v === 'all' ? 'Të gjithë liderët' : v}
                </Typography>
              </Stack>
            )}>
            <MenuItem value="all">Të gjithë liderët</MenuItem>
            {leadOptions.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </Select>
        )}

        {hasFilter && (
          <Button onClick={() => { setFStatus('all'); setFProject('all'); setFDept('all'); setFLead('all'); setFSearch(''); }}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderRadius: 2, height: 40,
              color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
            Pastro filtrat
          </Button>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: 'auto' }}>
          <Button onClick={exportList} startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderRadius: 2, height: 40, px: 1.75,
              color: 'text.primary', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'background.paper' } }}>
            Eksporto listën
          </Button>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {filtered.length}{filtered.length !== derived.length ? ` / ${derived.length}` : ''} punëtorë
          </Typography>
        </Stack>
      </Stack>

      {/* ── Agjentët desktop (vetëm kur ka) ──────────────────────────────── */}
      {agents.length > 0 && (
        <Paper elevation={0} sx={{ p: 1.75, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{
            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'text.secondary', mb: 1,
          }}>
            Agjentë desktop · {agents.filter(a => a.online).length}/{agents.length} online
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {agents.map(a => (
              <Stack key={a.deviceId} direction="row" spacing={0.875} alignItems="center"
                sx={{ px: 1.25, py: 0.625, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: a.online ? 'success.main' : 'grey.400' }} />
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                  {a.workerName || a.hostname || (a.deviceId || '').slice(0, 8)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {a.online ? a.status : 'offline'}{a.activeApp ? ` · ${a.activeApp}` : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* ── Kartelat e punëtorëve ────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(304px, 1fr))' }}>
        {filtered.map(({ id, w, p, status, workM, workerProjects, deptName }) => {
          const cfg = STATUS_CFG[status] || STATUS_CFG.absent;
          const name = `${w.FirstName || ''} ${w.LastName || ''}`.trim();
          const init = name.split(' ').filter(Boolean).map(x => x[0]).join('').slice(0, 2).toUpperCase();
          const breakM = calcBreakMinutes(p.breaks || []) + activeBreakMinutes(p);
          const onLeave = status === 'leave';
          const isActive = !!p.checkIn && !p.checkOut && !onLeave;
          const projectNames = workerProjects.map(pr => pr.name || pr.Name).filter(Boolean);

          return (
            <Paper key={id} elevation={0} sx={{
              p: 1.75, borderRadius: 3, border: '1px solid', borderColor: 'divider',
              transition: theme.motion.hover,
              '&:hover': { borderColor: cfg.border },
            }}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Avatar sx={{
                  width: 38, height: 38, flexShrink: 0, bgcolor: cfg.bg, color: cfg.color,
                  fontSize: '0.8125rem', fontWeight: 700, border: '1px solid', borderColor: cfg.border,
                }}>{init}</Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.25, color: 'text.primary' }} noWrap>
                    {name || '—'}
                  </Typography>
                  {w.Position && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.125 }} noWrap>
                      {w.Position}
                    </Typography>
                  )}
                </Box>
                <Chip size="small"
                  icon={React.cloneElement(cfg.icon, { sx: { fontSize: '12px !important', color: `${cfg.color} !important` } })}
                  label={cfg.label}
                  sx={{
                    bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.6875rem',
                    height: 22, border: 0, flexShrink: 0,
                    '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.625, color: `${cfg.color} !important` },
                  }} />
              </Stack>

              {(deptName || projectNames.length > 0) && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
                  {deptName && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <BusinessIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 110 }} noWrap>
                        {deptName}
                      </Typography>
                    </Stack>
                  )}
                  {projectNames.slice(0, 1).map(pn => (
                    <Chip key={pn} label={pn} size="small" sx={{
                      height: 20, fontSize: '0.6875rem', fontWeight: 600, maxWidth: 130,
                      bgcolor: 'primary.soft', color: 'primary.onSoft', border: 0,
                      '& .MuiChip-label': { px: 0.75 },
                    }} />
                  ))}
                  {projectNames.length > 1 && (
                    <Tooltip title={projectNames.slice(1).join(', ')}>
                      <Chip label={`+${projectNames.length - 1}`} size="small" sx={{
                        height: 20, fontSize: '0.6875rem', fontWeight: 600,
                        bgcolor: 'grey.100', color: 'text.secondary', border: 0,
                        '& .MuiChip-label': { px: 0.75 },
                      }} />
                    </Tooltip>
                  )}
                </Stack>
              )}

              {onLeave ? (
                <Stack direction="row" spacing={0.75} alignItems="center"
                  sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  <BeachAccessIcon sx={{ fontSize: 15, color: STATUS_CFG.leave.color }} />
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: STATUS_CFG.leave.color }}>
                    Me leje sot
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center"
                  sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  {[{ label: 'Hyrja', value: fmtTime(p.checkIn) }, { label: 'Dalja', value: fmtTime(p.checkOut) }].map(t => (
                    <Stack key={t.label} direction="row" spacing={0.75} alignItems="baseline">
                      <Typography sx={{
                        fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.disabled',
                      }}>{t.label}</Typography>
                      <Typography sx={{
                        fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: t.value ? 'text.primary' : 'text.disabled',
                      }}>{t.value || '—'}</Typography>
                    </Stack>
                  ))}
                  {breakM > 0 && (
                    <Stack direction="row" spacing={0.75} alignItems="baseline">
                      <Typography sx={{
                        fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.disabled',
                      }}>Pauzë</Typography>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: STATUS_CFG.break.color, fontVariantNumeric: 'tabular-nums' }}>
                        {breakM}m
                      </Typography>
                    </Stack>
                  )}
                  {p.checkIn && (
                    <Typography sx={{
                      ml: 'auto', fontSize: '0.8125rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                      color: isActive ? STATUS_CFG.present.color : 'text.secondary',
                    }}>
                      {isActive ? <LiveTimer checkIn={p.checkIn} /> : formatHHMMSS(workM * 60)}
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>
          );
        })}

        {filtered.length === 0 && !loading && (
          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: 'grey.100', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
              <PersonOffIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
              {hasFilter ? 'Asnjë punëtor nuk përputhet me filtrat' : 'Nuk ka të dhëna'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
